import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/layout/Header';
import { useAuth } from '@/App';
import {
  createProductReview,
  getFarmer,
  getProduct,
  getProductReviews,
} from '@/lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  Package,
  MapPin,
  User,
  Share2,
  ShoppingCart,
  Star,
  X,
} from 'lucide-react';

const Stars = ({ rating = 0, interactive = false, onChange }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((value) => (
      <button
        key={value}
        type="button"
        disabled={!interactive}
        onClick={() => interactive && onChange?.(value)}
        className={interactive ? 'cursor-pointer' : 'cursor-default'}
        aria-label={interactive ? `Rate ${value} stars` : undefined}
      >
        <Star
          className={`w-5 h-5 ${
            value <= Math.round(rating)
              ? 'fill-yellow-400 text-yellow-400'
              : 'text-gray-300'
          }`}
        />
      </button>
    ))}
  </div>
);

const ProductDetail = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const { addToCart, beginCheckout } = useAuth();

  const [product, setProduct] = useState(null);
  const [farmer, setFarmer] = useState(null);
  const [showFarmerProfile, setShowFarmerProfile] = useState(false);
  const [farmerPhotoIndex, setFarmerPhotoIndex] = useState(0);
  const farmerPhotoStripRef = useRef(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [imageIndex, setImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [reviewSummary, setReviewSummary] = useState({
    average_rating: null,
    review_count: 0,
  });
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    review_text: '',
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  const farmerPhotos = farmer
    ? [...new Set([farmer.photo_url, ...(farmer.photo_urls || [])].filter(Boolean))].slice(0, 2)
    : [];

  useEffect(() => {
    if (!showFarmerProfile) return;
    setFarmerPhotoIndex(0);
    if (farmerPhotoStripRef.current) {
      farmerPhotoStripRef.current.scrollTo({ left: 0, behavior: 'auto' });
    }
  }, [showFarmerProfile]);

  const moveFarmerPhoto = (event, direction) => {
    event.stopPropagation();
    if (farmerPhotos.length < 2) return;
    const nextIndex = (farmerPhotoIndex + direction + farmerPhotos.length) % farmerPhotos.length;
    setFarmerPhotoIndex(nextIndex);
    const strip = farmerPhotoStripRef.current;
    if (strip) {
      strip.scrollTo({ left: strip.clientWidth * nextIndex, behavior: 'smooth' });
    }
  };

  const handleFarmerPhotoScroll = () => {
    const strip = farmerPhotoStripRef.current;
    if (!strip || !strip.clientWidth) return;
    const nextIndex = Math.round(strip.scrollLeft / strip.clientWidth);
    if (nextIndex !== farmerPhotoIndex) setFarmerPhotoIndex(nextIndex);
  };

  const loadReviews = async () => {
    try {
      const response = await getProductReviews(productId);
      setReviews(response.data.reviews || []);
      setReviewSummary({
        average_rating: response.data.average_rating,
        review_count: response.data.review_count || 0,
      });
    } catch (error) {
      console.error('Failed to load reviews:', error);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [productRes, reviewsRes] = await Promise.all([
          getProduct(productId),
          getProductReviews(productId),
        ]);
        const productData = productRes.data;

        setProduct(productData);
        setImageIndex(0);
        if (productData.variants?.length) {
          setSelectedVariantId((current) =>
            productData.variants.some((variant) => variant.variant_id === current)
              ? current
              : productData.variants[0].variant_id
          );
        }
        setReviews(reviewsRes.data.reviews || []);
        setReviewSummary({
          average_rating: reviewsRes.data.average_rating,
          review_count: reviewsRes.data.review_count || 0,
        });

        if (productData.farmer_slug) {
          const farmerRes = await getFarmer(productData.farmer_slug);
          setFarmer(farmerRes.data);
        }
      } catch (err) {
        console.error(err);
        toast.error('Product not found');
      } finally {
        setLoading(false);
      }
    };

    load();

    const refresh = setInterval(async () => {
      try {
        const productRes = await getProduct(productId);
        setProduct(productRes.data);
        // A pack size may have been removed in Admin while this page is open.
        // Never keep a selection that is no longer offered by the server.
        setSelectedVariantId((current) => {
          const currentVariants = productRes.data.variants || [];
          if (!currentVariants.length) return null;
          return currentVariants.some((variant) => variant.variant_id === current)
            ? current
            : currentVariants[0].variant_id;
        });
      } catch (err) {
        console.error('Failed to refresh inventory:', err);
      }
    }, 10000);

    return () => clearInterval(refresh);
  }, [productId]);

  const handleAddToCart = () => {
    const selectedVariant =
      product?.variants?.find((variant) => variant.variant_id === selectedVariantId) ||
      product?.variants?.[0] ||
      null;
    const availablePacks = Number(
      selectedVariant?.sellable_pack_count ?? product?.sellable_pack_count ?? 0
    );

    if (!availablePacks) {
      toast.error('This pack size is currently out of stock');
      return;
    }

    addToCart(
      {
        ...product,
        farmer,
        selected_variant: selectedVariant,
      },
      quantity
    );
    beginCheckout();
  };

  const shareProduct = async () => {
    const url =
      window.location.hostname === 'localhost'
        ? window.location.href
        : `https://0middle.com/product/${product.slug}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: `${product.name} from ${farmer?.name || '0middle'}`,
          text: `${product.name} · ${selectedVariant?.pack_size || product.pack_size} · ₹${listPrice}`,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Product link copied');
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        toast.error('Unable to share product link');
      }
    }
  };

  const submitReview = async () => {
    if (reviewForm.review_text.trim().length < 2) {
      toast.error('Please add a short review');
      return;
    }

    setSubmittingReview(true);

    try {
      await createProductReview(productId, reviewForm);
      toast.success('Thanks for your review');
      setReviewForm({
        rating: 5,
        review_text: '',
      });
      await loadReviews();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          'Unable to submit review'
      );
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          Loading...
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-6xl mx-auto px-4 py-16 text-center">
          <h2 className="text-2xl font-semibold">Product not found</h2>
        </div>
      </div>
    );
  }

  // Admin pricing model:
  // E (Total Price) = Farmer + Packaging + Shipping + Testing
  // G (List Price)  = Total Price + Platform / Platform fee
  const selectedVariant =
    product.variants?.find((variant) => variant.variant_id === selectedVariantId) ||
    product.variants?.[0] ||
    {
      variant_id: 'default',
      pack_size: product.pack_size,
      farmer_price: product.farmer_price,
      platform_fee: product.platform_fee,
      original_platform_fee: product.original_platform_fee,
      platform_fee_discount: product.platform_fee_discount,
      shipping: product.shipping,
      packaging: product.packaging ?? product.handling,
      testing: product.testing,
      sellable_pack_count: product.sellable_pack_count,
    };

  const packaging = Number(selectedVariant.packaging ?? selectedVariant.handling ?? 0);
  const shipping = Number(selectedVariant.shipping || 0);
  const testing = Number(selectedVariant.testing || 0);
  const farmerPrice = Number(selectedVariant.farmer_price || 0);
  const transactionCost = Number(selectedVariant.platform_fee || 0);
  const platformFeeDiscount = Number(selectedVariant.platform_fee_discount || 0);
  const originalPlatformFee = Number(selectedVariant.original_platform_fee ?? (transactionCost + platformFeeDiscount));

  const totalPrice = farmerPrice + packaging + shipping + testing;
  const listPrice = totalPrice + transactionCost;
  const total = totalPrice * quantity + transactionCost;
  const maxPacks = Number(selectedVariant.sellable_pack_count || 0);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <Button variant="ghost" onClick={() => navigate('/browse')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Browse
          </Button>

          <Button variant="outline" onClick={shareProduct}>
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="relative rounded-2xl overflow-hidden bg-white border border-border/40">
            {(() => {
              const images = product.image_urls?.length
                ? product.image_urls
                : product.image_url
                  ? [product.image_url]
                  : [];

              if (!images.length) {
                return (
                  <div className="w-full min-h-[420px] flex items-center justify-center bg-muted">
                    <Package className="w-16 h-16 text-muted-foreground/30" />
                  </div>
                );
              }

              const safeIndex = Math.min(imageIndex, images.length - 1);

              return (
                <>
                  <div className="w-full flex items-center justify-center p-3 min-h-[420px]">
                    <img
                      src={images[safeIndex]}
                      alt={`${product.name} ${safeIndex + 1}`}
                      className="block max-w-full w-auto h-auto max-h-[70vh] object-contain"
                    />
                  </div>

                  {images.length > 1 && (
                    <>
                      <button
                        type="button"
                        aria-label="Previous product image"
                        onClick={() =>
                          setImageIndex((current) =>
                            (current - 1 + images.length) % images.length
                          )
                        }
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 border border-border shadow-md flex items-center justify-center text-foreground hover:bg-white"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>

                      <button
                        type="button"
                        aria-label="Next product image"
                        onClick={() =>
                          setImageIndex((current) =>
                            (current + 1) % images.length
                          )
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 border border-border shadow-md flex items-center justify-center text-foreground hover:bg-white"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>

                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                        {images.map((_, index) => (
                          <button
                            key={index}
                            type="button"
                            aria-label={`Show product image ${index + 1}`}
                            onClick={() => setImageIndex(index)}
                            className={`w-2.5 h-2.5 rounded-full border border-white shadow ${
                              index === safeIndex ? 'bg-primary' : 'bg-primary/25'
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              );
            })()}

            {product.category && (
              <Badge className="absolute top-4 left-4">
                {product.category}
              </Badge>
            )}
          </div>

          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-semibold mb-2">
                {product.name}
              </h1>

              <button
                type="button"
                onClick={() => farmer && setShowFarmerProfile(true)}
                className="inline-flex items-center gap-3 rounded-xl border border-primary/15 bg-primary/5 px-3 py-2 mb-3 hover:bg-primary/10 transition-colors text-left"
                aria-label={farmer ? `Meet Farmer ${farmer.name}` : 'Farmer'}
              >
                <div className="w-9 h-9 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center shrink-0">
                  {(farmer?.photo_urls?.[0] || farmer?.photo_url) ? (
                    <img
                      src={farmer.photo_url}
                      alt={farmer.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-4 h-4 text-primary" />
                  )}
                </div>

                <div className="text-sm leading-tight">
                  <p className="font-semibold">
                    {farmer?.name || product.farmer_slug}
                  </p>
                  {(farmer?.city || farmer?.village || farmer?.district) && (
                    <p className="text-muted-foreground flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      {[farmer?.city || farmer?.village, farmer?.district]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  )}
                </div>
              </button>

              <div className="flex items-center gap-2 mb-3">
                {reviewSummary.review_count > 0 ? (
                  <>
                    <Stars rating={reviewSummary.average_rating} />
                    <span className="text-sm font-medium">
                      {reviewSummary.average_rating}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      ({reviewSummary.review_count} reviews)
                    </span>
                  </>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    No reviews yet
                  </span>
                )}
              </div>

              <div className="text-muted-foreground space-y-1">
                {(product.description || '').split(/\s*[;|]\s*|\r?\n/).map((part) => part.trim()).filter(Boolean).map((highlight, index) => (
                  <p key={index}>{highlight}</p>
                ))}
              </div>

              {product.inventory_available != null && (
                <p className="text-sm font-medium text-primary mt-3">
                  {product.inventory_available} {product.inventory_unit} available
                  {maxPacks != null
                    ? ` · ${maxPacks} packs of ${selectedVariant.pack_size || product.pack_size}`
                    : ''}
                </p>
              )}

            </div>

            <Card>
              <CardContent className="p-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                  <div>
                    <label className="block text-sm font-medium mb-1">Pack size</label>
                    <select
                      className="w-full border rounded-md h-10 px-3 bg-background"
                      value={selectedVariant.variant_id}
                      onChange={(e) => {
                        setSelectedVariantId(e.target.value);
                        setQuantity(1);
                      }}
                    >
                      {(product.variants?.length ? product.variants : [selectedVariant]).map((variant) => (
                        <option key={variant.variant_id} value={variant.variant_id}>
                          {variant.pack_size} · ₹{variant.list_price ?? (
                            Number(variant.farmer_price || 0) +
                            Number(variant.packaging ?? variant.handling ?? 0) +
                            Number(variant.shipping || 0) +
                            Number(variant.testing || 0) +
                            Number(variant.platform_fee || 0)
                          )}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Quantity</label>
                    <Input
                      type="number"
                      min="1"
                      max={maxPacks}
                      disabled={maxPacks === 0}
                      value={quantity}
                      onChange={(e) =>
                        setQuantity(
                          Math.min(maxPacks || 1, Math.max(1, Number(e.target.value)))
                        )
                      }
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="border-t pt-4 space-y-2">
                  <h3 className="font-medium mb-3">
                    Transparent Price Breakdown
                  </h3>

                  <div className="flex justify-between">
                    <span className="flex flex-col">
                      <span>To the farmer</span>
                      <span className="text-xs italic text-muted-foreground">(includes processing, packaging and shipping)</span>
                    </span>
                    <span>₹{totalPrice * quantity}</span>
                  </div>

                  {platformFeeDiscount > 0 ? (
                    <>
                      <div className="flex justify-between gap-3">
                        <span>Original 0middle platform fee</span>
                        <span className="line-through text-muted-foreground">₹{originalPlatformFee}</span>
                      </div>
                      <div className="flex justify-between gap-3 font-medium text-green-700">
                        <span>0middle platform fee discount</span>
                        <span>−₹{platformFeeDiscount}</span>
                      </div>
                      <p className="rounded-lg bg-green-50 p-3 text-sm leading-6 text-green-900">
                        {transactionCost === 0
                          ? `0middle has waived its ₹${platformFeeDiscount} platform fee for this order.`
                          : `0middle has reduced its platform fee by ₹${platformFeeDiscount} for this order.`}
                        {' '}The farmer's amount is unchanged.
                      </p>
                    </>
                  ) : (
                    <div className="flex justify-between">
                      <span>Flat Platform Fee</span>
                      <span>₹{transactionCost}</span>
                    </div>
                  )}

                  <div className="flex justify-between border-t pt-3 text-lg font-semibold">
                    <span>Total</span>
                    <span className="text-primary">₹{total}</span>
                  </div>
                </div>

                <Button
                  onClick={handleAddToCart}
                  disabled={maxPacks === 0}
                  className="w-full h-12"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  {maxPacks === 0 ? 'Out of stock' : 'Buy'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        <section className="mt-12">
          <div className="flex items-center gap-3 mb-5">
            <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
            <h2 className="text-2xl font-semibold">Customer Reviews</h2>
            <Badge variant="secondary">
              {reviewSummary.review_count}
            </Badge>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              {reviews.length === 0 && (
                <Card>
                  <CardContent className="p-5 text-muted-foreground">
                    No reviews yet. Be the first verified customer to review this product.
                  </CardContent>
                </Card>
              )}

              {reviews.map((review) => (
                <Card key={review.review_id}>
                  <CardContent className="p-5">
                    <div className="flex justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">
                            {review.reviewer_name}
                          </p>
                          {review.verified_purchase && (
                            <Badge variant="outline">
                              <Check className="w-3 h-3 mr-1" />
                              Verified Purchase
                            </Badge>
                          )}
                        </div>
                        <Stars rating={review.rating} />
                      </div>

                      {review.created_at && (
                        <span className="text-xs text-muted-foreground">
                          {new Date(review.created_at).toLocaleDateString('en-IN')}
                        </span>
                      )}
                    </div>

                    <p className="mt-3 text-sm">
                      {review.review_text}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card>
              <CardContent className="p-5 space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">Write a review</h3>
                </div>

                <div>
                  <label className="text-sm font-medium">Rating</label>
                  <div className="mt-2">
                    <Stars
                      interactive
                      rating={reviewForm.rating}
                      onChange={(rating) =>
                        setReviewForm({ ...reviewForm, rating })
                      }
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium">Review</label>
                  <textarea
                    value={reviewForm.review_text}
                    onChange={(e) =>
                      setReviewForm({
                        ...reviewForm,
                        review_text: e.target.value,
                      })
                    }
                    rows={4}
                    maxLength={2000}
                    className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
                    placeholder="How was the product?"
                  />
                </div>

                <Button
                  className="w-full"
                  disabled={submittingReview}
                  onClick={submitReview}
                >
                  {submittingReview ? 'Submitting...' : 'Submit review'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      {showFarmerProfile && farmer && (
        <div
          className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4"
          onClick={() => setShowFarmerProfile(false)}
          role="presentation"
        >
          <div
            className="relative bg-card rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Meet Farmer ${farmer.name}`}
          >
            <button
              type="button"
              onClick={() => setShowFarmerProfile(false)}
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-background/90 border flex items-center justify-center hover:bg-muted"
              aria-label="Close farmer profile"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="grid md:grid-cols-2">
              <div className="relative bg-muted overflow-hidden">
                {farmerPhotos.length > 0 ? (
                  <>
                    <div
                      ref={farmerPhotoStripRef}
                      onScroll={handleFarmerPhotoScroll}
                      className="flex w-full overflow-x-auto snap-x snap-mandatory scroll-smooth"
                      style={{ WebkitOverflowScrolling: 'touch' }}
                    >
                      {farmerPhotos.map((imageUrl, index) => (
                        <div
                          key={`${imageUrl}-${index}`}
                          className="w-full min-w-full snap-center flex items-center justify-center bg-muted"
                        >
                          <img
                            src={imageUrl}
                            alt={`${farmer.name} ${index + 1}`}
                            className="block w-full h-auto object-contain"
                          />
                        </div>
                      ))}
                    </div>

                    {farmerPhotos.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={(event) => moveFarmerPhoto(event, -1)}
                          className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/75 border border-white/60 shadow-sm flex items-center justify-center text-foreground/75 hover:bg-white/90"
                          aria-label="Previous farmer photo"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                          type="button"
                          onClick={(event) => moveFarmerPhoto(event, 1)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/75 border border-white/60 shadow-sm flex items-center justify-center text-foreground/75 hover:bg-white/90"
                          aria-label="Next farmer photo"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-1.5 rounded-full bg-black/20 px-2 py-1">
                          {farmerPhotos.map((_, index) => (
                            <span
                              key={index}
                              className={`w-1.5 h-1.5 rounded-full ${index === farmerPhotoIndex ? 'bg-white' : 'bg-white/50'}`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full min-h-[260px] md:min-h-[480px] flex items-center justify-center">
                    <User className="w-20 h-20 text-muted-foreground/40" />
                  </div>
                )}
              </div>

              <div className="p-6 md:p-10 flex flex-col justify-center">
                {farmer.headline && (
                  <h2 className="text-xl md:text-2xl font-extrabold leading-tight mb-5 text-foreground">
                    {farmer.headline}
                  </h2>
                )}
                {farmer.story && (
                  <div className="text-sm md:text-base leading-7 text-foreground/80 whitespace-pre-line">
                    {farmer.story}
                  </div>
                )}
                {(farmer.city || farmer.village || farmer.district || farmer.state) && (
                  <div className="mt-6 pt-4 border-t flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    {[farmer.city || farmer.village, farmer.district, farmer.state]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
